// src/providers/authProvider.ts
import { AuthProvider } from 'react-admin';
import { clearAuthData, getCurrentRole } from '@/utils/auth';
import { UserRole } from '@app-types/models';
import keycloak, { storeKeycloakSession } from '@/auth/keycloak';

const PORTAL_URL = import.meta.env.VITE_PORTAL_URL || 'https://portal.roommatik.com';

// ✅ Event emitter para notificar cambios de autenticación
export const authEvents = new EventTarget();

export const AUTH_EVENTS = {
  LOGIN_SUCCESS: 'auth:login:success',
  LOGOUT: 'auth:logout',
  ROLE_CHANGED: 'auth:role:changed',
} as const;

/**
 * Authentication provider for React Admin (Keycloak-only).
 *
 * Login is handled by keycloak-js before the app renders (index.tsx).
 * This provider is a thin wrapper that keeps the token fresh and handles logout.
 */
const authProvider: AuthProvider = {

  // Login is handled externally by keycloak-js — this is never called
  login: async () => Promise.resolve(),

  logout: async () => {
    const previousRole = localStorage.getItem('role');

    clearAuthData();
    localStorage.removeItem('hotelName');
    localStorage.removeItem('groupName');
    localStorage.removeItem('selectedHotelId');
    localStorage.removeItem('selectedHotelData');

    authEvents.dispatchEvent(new CustomEvent(AUTH_EVENTS.LOGOUT, {
      detail: { previousRole }
    }));

    keycloak.logout({ redirectUri: PORTAL_URL });

    // Return false so React Admin doesn't try to navigate on its own
    return false as unknown as void;
  },

  checkAuth: async () => {
    if (!keycloak.authenticated) {
      return Promise.reject({ message: 'Not authenticated', redirectTo: false });
    }

    try {
      const refreshed = await keycloak.updateToken(30);
      if (refreshed) {
        storeKeycloakSession(keycloak);
      }
      return Promise.resolve();
    } catch {
      clearAuthData();
      globalThis.location.href = PORTAL_URL;
      return Promise.reject({ message: 'Session expired', redirectTo: false });
    }
  },

  checkError: async (error: any) => {
    const status = error?.status || error?.response?.status;

    if (status === 401 || status === 403) {
      try {
        await keycloak.updateToken(30);
        storeKeycloakSession(keycloak);
        return Promise.resolve();
      } catch {
        clearAuthData();
        globalThis.location.href = PORTAL_URL;
        return Promise.reject({ redirectTo: false });
      }
    }

    return Promise.resolve();
  },

  getPermissions: async () => {
    // getCurrentRole() returns the impersonated role while active — every menu/guard in the
    // app hangs off usePermissions(), so this is the single choke point for the effective role.
    const role = getCurrentRole();
    if (!role) {
      return Promise.reject({ message: 'No role found' });
    }
    return Promise.resolve(role as UserRole);
  },

  getIdentity: async () => {
    const parsed = keycloak.tokenParsed as any;
    if (!parsed) {
      return Promise.reject({ message: 'No user identity found' });
    }

    return Promise.resolve({
      id: parsed.sub ?? '',
      fullName: parsed.name || parsed.preferred_username || 'Usuario',
      avatar: undefined,
      email: parsed.email,
      role: localStorage.getItem('role') || undefined,
      hotelId: localStorage.getItem('hotelId') || undefined,
      groupId: localStorage.getItem('groupId') || undefined,
    });
  },

  handleCallback: async () => Promise.resolve(),
};

export default authProvider;
